/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import { AIGenerateIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import Badge from 'bundles/unified-description-page-common/components/shared/badges/Badge';
import { getNewAiSkillsBadgeContent } from 'bundles/unified-description-page-common/utils/getNewAiSkillsBadgeContent';
import { getPartnerRefreshAiBadgeTitle } from 'bundles/unified-description-page-common/utils/getPartnerRefreshAiBadgeTitle';
import {
  GOOGLE_REFRESH_CERTIFICATE_SLUGS,
  IBM_REFRESH_CERTIFICATE_SLUGS,
  shouldShowNewAiSkillsBadge,
} from 'bundles/unified-description-page-common/utils/shouldShowNewAiSkillsBadge';

import _t from 'i18n!nls/unified-description-page-common';

const styles = {
  svg: css`
    margin-bottom: -2px;
  `,
  button: css`
    padding: 0;
    vertical-align: baseline;
  `,
  subtitle: css`
    line-height: 1.5;
    margin-bottom: var(--cds-spacing-300);
  `,
  content: css`
    ul {
      padding-left: 1.5em;

      li {
        list-style: disc;
      }
    }
  `,
};

const { HeadingGroup, Content, Actions } = Dialog;

type Props = {
  slug: string | undefined;
  disabled?: boolean;
} & Pick<React.ComponentProps<typeof Badge>, 'designStyle'>;

const NewAiBadge = ({ slug, disabled, designStyle }: Props) => {
  const track = useTracker();
  const [isOpened, setIsOpened] = useState(false);

  const handleClick = () => {
    setIsOpened(true);

    track('click_button', {
      button: {
        name: 'xdp_gen_ai_modal',
      },
    });
  };

  const handleClose = () => {
    setIsOpened(false);
  };

  const shouldRenderNewAiSkillsBadge = shouldShowNewAiSkillsBadge(slug);

  const getSubheadingProfession = () => {
    if (slug === 'google-data-analytics') {
      return _t('data analytics');
    }
    if (slug === 'google-digital-marketing-ecommerce') {
      return _t('digital marketing & e-commerce');
    }
    if (slug === 'google-it-support') {
      return _t('IT support');
    }
    if (slug === 'google-project-management') {
      return _t('project management');
    }
    if (slug === 'google-ux-design') {
      return _t('UX design');
    }
    if (slug === 'google-cybersecurity') {
      return _t('cybersecurity');
    }

    return null;
  };

  if (!shouldRenderNewAiSkillsBadge) {
    return null;
  }

  const generateCopy = () => {
    if (slug && IBM_REFRESH_CERTIFICATE_SLUGS.includes(slug)) {
      return _t('Learn how to:');
    }
    if (slug && GOOGLE_REFRESH_CERTIFICATE_SLUGS.includes(slug)) {
      return (
        <FormattedMessage
          message={_t('This Professional Certificate includes 6 new videos on how to use AI in {profession}.')}
          profession={getSubheadingProfession()}
        />
      );
    }

    if (slug === 'ai-essentials-google') {
      return _t(
        'This Specialization includes videos on how to use generative AI tools to boost productivity and innovation to:'
      );
    }
    if (slug === 'prompting-essentials-google') {
      return _t(
        "This Specialization includes videos on how to master the art of prompting to maximize AI's potential to:"
      );
    }
    return null;
  };

  return (
    <>
      <Button size="small" variant="ghost" css={styles.button} onClick={handleClick} disabled={disabled}>
        <Badge designStyle={designStyle}>
          <Typography2 variant="inherit" color="inherit" component="p">
            <AIGenerateIcon size="small" css={styles.svg} />
            {_t('New AI skills')}
          </Typography2>
        </Badge>
      </Button>
      <Dialog open={isOpened} onClose={handleClose}>
        <HeadingGroup>{getPartnerRefreshAiBadgeTitle(slug)}</HeadingGroup>
        <Content>
          <Typography2 variant="subtitleMedium" component="p" css={styles.subtitle}>
            {generateCopy()}
          </Typography2>
          <Typography2 variant="bodyPrimary" component="span" css={styles.content}>
            {getNewAiSkillsBadgeContent(slug)}
          </Typography2>
        </Content>
        <Actions />
      </Dialog>
    </>
  );
};

export default NewAiBadge;
